// apps/web/src/components/analytics/occupancy-tab.tsx
import type { SeasonRegion } from '@sea-snag/shared';
import { type OccupancySeasonSummary, roundBookedWeeks } from '@sea-snag/shared';
import { Card, CardContent, Skeleton } from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CalendarCheck, CalendarDays, Euro, Ship } from 'lucide-react';

import { useOrganization } from '../../contexts/organization-context';
import { analyticsApi } from '../../lib/api-client';
import { bookedDaysByBoat, type OccupancySort, sortOccupancyBoats } from '../../lib/occupancy-sort';
import { queryKeys } from '../../lib/query-keys';
import { formatSeasonRange, presetSeasonLabel } from '../../lib/season-format';
import { inSeasonOn } from '../../lib/season-status';
import { StatCard } from '../stat-card';
import { OccupancyFilters } from './occupancy-filters';
import { OccupancyHeatmap } from './occupancy-heatmap';
import { SeasonBreakdownPanel } from './season-breakdown-panel';
import { SeasonOverviewStrip } from './season-overview-strip';

interface OccupancyTabProps {
  year: string;
  boatPid: string;
  /** Comma-separated boat pids from the URL; '' means the whole fleet. */
  boats: string;
  /** Owner pid, UNASSIGNED_OWNER, or '' for no owner filter. */
  owner: string;
  sort: OccupancySort;
}

/**
 * The second line under "Avg booked weeks / boat".
 *
 * The averages deliberately cover only boats with a target, so the card has to
 * say so whenever that is fewer than all of them — otherwise the number looks
 * like a fleet average and quietly is not.
 */
function targetHint(totals: OccupancySeasonSummary['totals'] | undefined): string | undefined {
  if (!totals) return undefined;
  if (totals.boatsWithTarget === 0) {
    return `No boat goals set for ${totals.boatsTotal} boat${totals.boatsTotal === 1 ? '' : 's'}`;
  }
  const achievement = `${totals.avgTargetAchievementPct}% of target`;
  if (totals.boatsWithTarget === totals.boatsTotal) return achievement;
  return `${achievement} · averages include ${totals.boatsWithTarget} of ${totals.boatsTotal} boats`;
}

/**
 * Splits the year's booked weeks into the part inside the configured season and
 * the part outside it. The card's own value is the full year, so a fleet that
 * charters out of season can see how much of its year that is instead of a
 * single figure that hides it — and why occupancy, which counts the season
 * only, can look low against a healthy total.
 *
 * Out-of-season is the difference of the two displayed numbers rather than a
 * third figure from the API, so the three always add up. Rounding each of two
 * day counts to hundredths of a week can leave them a hundredth short of the
 * rounded total, which on a card reads as an error.
 */
function seasonSplitHint(totals: OccupancySeasonSummary['totals'] | undefined): string | undefined {
  if (!totals) return undefined;
  const inSeason = totals.bookedWeeksInSeason;
  const outOfSeason = roundBookedWeeks(totals.totalBookedWeeks - inSeason);
  const occupancy = `${totals.occupancyPct}% occupancy`;
  if (outOfSeason <= 0) return `all in season · ${occupancy}`;
  return `${inSeason} in season · ${outOfSeason} out of season · ${occupancy}`;
}

export function OccupancyTab({ year, boatPid, boats, owner, sort }: OccupancyTabProps) {
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const orgPid = organization?.pid ?? '';
  const region = (organization?.seasonRegion ?? 'mediterranean') as SeasonRegion;
  const boatParam = boatPid === 'all' ? undefined : boatPid;
  const selectedBoats = boats === '' ? [] : boats.split(',');
  // Omitted rather than sent empty: the API reads a present-but-empty list as
  // "nothing selected", and clearing the picker means "no filter".
  const boatPidsParam = selectedBoats.length > 0 ? boats : undefined;
  const ownerParam = owner === '' ? undefined : owner;

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.analytics.occupancySeason(
      orgPid,
      year,
      boatParam,
      boatPidsParam,
      ownerParam
    ),
    queryFn: () =>
      analyticsApi.occupancySeason({
        year,
        boatPid: boatParam,
        boatPids: boatPidsParam,
        ownerPid: ownerParam,
      }),
    enabled: !!orgPid,
  });

  // Sorting happens here and never reaches the API, so it cannot move a metric.
  const sortedBoats = sortOccupancyBoats(
    data?.boats ?? [],
    bookedDaysByBoat(data?.heatmap ?? []),
    sort
  );

  // The season as actually operated: configured dates widened by bookings.
  const { data: season } = useQuery({
    queryKey: queryKeys.analytics.seasonOverview(orgPid, year),
    queryFn: () => analyticsApi.seasonOverview({ year }),
    enabled: !!orgPid,
  });

  const totals = data?.totals;
  // Income carries its own currency: the season-scoped revenue can be empty for
  // an org booking entirely off-season, and the card would then be formatted
  // with a defaulted one. Rounding to whole units happens here, once — the API
  // returns the exact amount so this page and Season Progress cannot drift.
  const incomeFmt = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: totals?.incomeCurrency ?? totals?.currency ?? 'EUR',
    maximumFractionDigits: 0,
  });

  const isFiltered = selectedBoats.length > 0 || owner !== '';
  // Y comes from the API, which counts the fleet the way the grid does — dead
  // listings excluded. The boats endpoint would include boats never shown here.
  const fleetCount = totals?.fleetBoatCount ?? 0;
  // Y in "Showing X of Y boats" is the unfiltered fleet, which the filter's own
  // boat list already holds — so the count costs no extra request.
  const shownCount = data?.boats.length ?? 0;

  return (
    <div className="space-y-6">
      {/* Season overview strip, the same as the dashboard's Season Progress widget */}
      <Card>
        <CardContent className="pt-6">
          <SeasonOverviewStrip orgPid={orgPid} year={year} region={region} />
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="space-y-2">
        <OccupancyFilters
          orgPid={orgPid}
          selectedBoats={selectedBoats}
          owner={owner}
          sort={sort}
          onChange={(next) =>
            void navigate({
              to: '.',
              search: (prev) => ({
                ...prev,
                ...(next.boats !== undefined ? { boats: next.boats.join(',') } : {}),
                ...(next.owner !== undefined ? { owner: next.owner } : {}),
                ...(next.sort !== undefined ? { sort: next.sort } : {}),
              }),
              replace: true,
            })
          }
        />
        {isFiltered && fleetCount > 0 && (
          <p className="text-muted-foreground text-xs">
            Showing {shownCount} of {fleetCount} boats. Every figure below describes just these.
          </p>
        )}
      </div>

      {/* Metric cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[80px]" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label="Total booked weeks"
            value={String(totals?.totalBookedWeeks ?? 0)}
            hint={seasonSplitHint(totals)}
            icon={CalendarCheck}
          />
          <StatCard
            label="Avg booked weeks / boat"
            value={
              totals?.avgBookedWeeksPerBoat === null || totals === undefined
                ? 'No targets set'
                : String(totals.avgBookedWeeksPerBoat)
            }
            hint={targetHint(totals)}
            icon={Ship}
          />
          <StatCard
            label="Total income"
            value={incomeFmt.format(totals?.totalIncome ?? 0)}
            hint="confirmed charters this year"
            icon={Euro}
          />
        </div>
      )}

      {/* The year split by the season — hides itself when nothing falls outside */}
      {!isLoading && data && (
        <SeasonBreakdownPanel
          breakdown={data.breakdown}
          seasonLabel={
            season ? formatSeasonRange(season.startDate, season.endDate) : presetSeasonLabel(region)
          }
        />
      )}

      {/* Per-boat heatmap */}
      {isLoading ? (
        <Skeleton className="h-[300px]" />
      ) : data && data.boats.length > 0 ? (
        <OccupancyHeatmap
          year={year}
          boats={sortedBoats}
          heatmap={data.heatmap}
          spans={data.spans}
          weekPrices={data.weekPrices}
          isInSeasonColumn={(day) => inSeasonOn(day, region, season)}
        />
      ) : (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            <CalendarDays className="mx-auto mb-2 h-6 w-6 opacity-50" />
            {isFiltered ? 'No boats match these filters.' : 'No boats in this fleet yet.'}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
